import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { DataTable, EmptyState, LoadingButton, Notice, PageHeader, StatCard, StatusChip, type StatusTone } from '@routes-plan/ui';
import PermissionButton from '../../../components/common/PermissionButton';
import StandardModal from '../../../components/modales/StandardModal';
import ClientChangeHistoryModal from '../../../components/common/ClientChangeHistoryModal';
import { AdminPage, ContentCard, FilterRow, RowAction, useAlert } from '../shared/AdminUi';
import AlertSignalsModal from './AlertSignalsModal';
import MenuItem from '@mui/material/MenuItem';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { formatDateTime } from '../devices/DeviceList';
import { useT, type TextKey } from '../../../i18n';
import { formatCurrency as companyMoney, formatNumber } from '../../../utils/format/currency';

/**
 * Administrar → Centro de alertas. Lo que necesita la revisión de un administrador: hoy, los cambios a datos
 * delicados de los clientes, y un resumen de los teléfonos y las correcciones de abonos por aprobar.
 * Quien causó la alerta no puede cerrarla (el API lo impide). Cada usuario ve solo los cobros de sus oficinas.
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Severity = 'high' | 'warning' | 'info';
type Filter = 'open' | 'resolved';

export interface AlertItem {
  id: number;
  kind: AlertKind;
  severity: Severity;
  status: 'open' | 'resolved';
  entity: { type: string; id: number };
  data:
    | ({ fields?: string[]; source?: string; from?: 'web' | 'app'; clientName?: string | null } & {
        operation?: 'payment' | 'loan' | 'refinance' | 'expense';
        action?: string;
        target?: string | null;
        limit?: 'discount' | 'refinance';
        max?: number;
        used?: number;
        adding?: number;
        entityId?: number;
        minutes?: number;
        meters?: number;
      })
    | null;
  events: number;
  createdAt: string;
  lastEventAt: string;
  firstActor: { id: number; username: string | null } | null;
  lastActor: { id: number; username: string | null } | null;
  resolvedBy: { id: number; username: string | null } | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  route: { id: number; name: string | null; office: string | null } | null;
  client: { id: number; fullName: string | null; idNumber: string | null } | null;
  /** Alertas antifraude: el representante que registró los abonos o préstamos. */
  salesRep?: { id: number; username: string | null; fullName: string | null } | null;
  /** admin_change: la persona que hizo el cambio. */
  user?: { id: number; username: string | null; fullName: string | null } | null;
}

/** client_changed: datos del cliente. El resto, antifraude por representante. */
export type AlertKind = 'client_changed' | 'mock_location' | 'clock_skew' | 'far_payment' | 'emulator' | 'daily_limit' | 'admin_change';
const KINDS: AlertKind[] = ['client_changed', 'mock_location', 'clock_skew', 'far_payment', 'emulator', 'daily_limit', 'admin_change'];

/** «Descuentos del día: 75.000 (tope 50.000)» para la alerta y cada señal de tope diario. */
export const describeDailyLimit = (
  d: { limit?: string; max?: number; used?: number; adding?: number } | null | undefined,
  t: (k: TextKey, v?: Record<string, string | number>) => string,
) => {
  if (!d || typeof d.max !== 'number') return t('alerts.kind.daily_limit');
  const total = Number(d.used || 0) + Number(d.adding || 0);
  const fmt = (n: number) => (d.limit === 'refinance' ? formatNumber(n) : companyMoney(n));
  return t(d.limit === 'refinance' ? 'alerts.signal.dailyRefinance' : d.limit === 'expense' ? 'alerts.signal.dailyExpense' : 'alerts.signal.dailyDiscount', { total: fmt(total), max: fmt(d.max) });
};

/** «GPS simulado · abono #123», «Hora atrasada 120 min», «Abono a 850 m de la casa». */
export const describeAlert = (a: AlertItem, t: (k: TextKey, v?: Record<string, string | number>) => string) => {
  if (a.kind === 'client_changed') return describeFields(a.data?.fields, t) || t('alerts.kind.client_changed');
  if (a.kind === 'admin_change') {
    const what = a.data?.action ? t(`alerts.adminChange.${a.data.action}` as TextKey) : t('alerts.kind.admin_change');
    return [what, a.data?.target].filter(Boolean).join(' · ');
  }
  const op = a.data?.operation && a.data?.entityId ? t(`alerts.signal.op.${a.data.operation}` as TextKey, { id: a.data.entityId }) : '';
  const base =
    a.kind === 'daily_limit'
      ? describeDailyLimit(a.data, t)
      : a.kind === 'clock_skew' && typeof a.data?.minutes === 'number'
      ? t(a.data.minutes < 0 ? 'alerts.signal.clockBehind' : 'alerts.signal.clockAhead', { min: Math.abs(a.data.minutes) })
      : a.kind === 'far_payment' && typeof a.data?.meters === 'number'
        ? t('alerts.signal.farPayment', { m: a.data.meters })
        : t(`alerts.kind.${a.kind}` as TextKey);
  return [base, op].filter(Boolean).join(' · ');
};

interface Summary {
  alerts: number;
  pendingDevices: number;
  pendingCorrections: number;
}

const SEVERITY_TONE: Record<Severity, StatusTone> = { high: 'danger', warning: 'warning', info: 'info' };

/** «Celular, Dirección» con los nombres de los campos en el idioma del usuario. */
export const describeFields = (fields: string[] | undefined, t: (k: TextKey) => string) =>
  (fields ?? []).map((f) => t(`clientChanges.field.${f}` as TextKey)).join(', ');

/** Quién causó la alerta: «rep.sur» o «rep.sur, sup.norte» si fueron dos personas. */
const actors = (a: AlertItem) =>
  Array.from(new Set([a.firstActor?.username, a.lastActor?.username].filter(Boolean))).join(', ') || '—';

const AlertCenter: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  setPageTitle('menu.admin.alerts');
  const t = useT();
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { canRead, canUpdate, isLoadingPermissions } = useResourcePermissions('/admin/alerts');
  const devicesPerm = useResourcePermissions('/admin/devices');
  const correctionsPerm = useResourcePermissions('/loans/correction-approvals');
  const [filter, setFilter] = useState<Filter>('open');
  // Tipo de alerta (vacío = todas).
  const [kind, setKind] = useState<AlertKind | ''>('');
  const [rows, setRows] = useState<AlertItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [dialog, setDialog] = useState<AlertItem | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState<AlertItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        axios.get<{ data: AlertItem[] }>('alerts', { params: { status: filter, ...(kind ? { kind } : {}) } }),
        axios.get<Summary>('alerts/summary'),
      ]);
      setRows(list.data.data);
      setSummary(sum.data);
    } catch {
      showAlert(t('alerts.loadError'), 'error');
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, [filter, kind, showAlert, t]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  const resolve = async () => {
    if (!dialog) return;
    setSaving(true);
    try {
      await axios.post(`alerts/${dialog.id}/resolve`, { note });
      showAlert(t('alerts.resolvedOk'), 'success');
    } catch (error) {
      const code = axios.isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined;
      const key: TextKey =
        code === 'SAME_PERSON' ? 'alerts.samePerson' : code === 'ALREADY_RESOLVED' ? 'alerts.alreadyResolved' : 'alerts.actionError';
      showAlert(t(key), 'error');
    } finally {
      setSaving(false);
      setDialog(null);
      await load();
    }
  };

  const subjectName = (a: AlertItem) =>
    a.salesRep || a.user
      ? (a.salesRep ?? a.user)!.fullName || (a.salesRep ?? a.user)!.username || `#${a.entity.id}`
      : a.client?.fullName ?? a.data?.clientName ?? `#${a.entity.id}`;
  const review = (a: AlertItem) =>
    a.resolvedBy ? [a.resolvedBy.username, formatDateTime(a.resolvedAt), a.resolutionNote].filter(Boolean).join(' · ') : '';

  if (!canRead && !isLoadingPermissions) {
    return (
      <AdminPage>
        <PageHeader title={t('alerts.title')} />
        <Notice severity="warning">{t('alerts.noReadPermission')}</Notice>
      </AdminPage>
    );
  }

  const cards = [
    { key: 'alerts', label: t('alerts.summaryAlerts'), value: summary?.alerts ?? 0, show: true, to: null as string | null, icon: <NotificationsActiveOutlinedIcon /> },
    { key: 'devices', label: t('alerts.summaryDevices'), value: summary?.pendingDevices ?? 0, show: devicesPerm.canRead, to: '/admin/devices', icon: <PhoneAndroidOutlinedIcon /> },
    {
      key: 'corrections',
      icon: <FactCheckOutlinedIcon />,
      label: t('alerts.summaryCorrections'),
      value: summary?.pendingCorrections ?? 0,
      show: correctionsPerm.canRead,
      to: '/loans/correction-approvals',
    },
  ].filter((c) => c.show);

  return (
    <AdminPage>
      <PageHeader
        title={t('alerts.title')}
        description={t('alerts.subtitle')}
        helpAction={<ScreenTourButton />}
        actions={
          <Button variant="outlined" color="inherit" startIcon={<RefreshIcon />} onClick={() => void load()} data-tour="alerts-refresh">
            {t('common.refresh')}
          </Button>
        }
      />
      <Notice severity="info" dismissible>
        {t('alerts.intro')}
      </Notice>
      <Box
        data-tour="alerts-summary"
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}
      >
        {cards.map((c) => {
          const card = (
            <StatCard
              label={c.label}
              value={c.value}
              icon={c.icon}
              loading={!summary && !loaded}
              hint={c.to ? t('alerts.open') : undefined}
            />
          );
          return c.to ? (
            <ButtonBase
              key={c.key}
              onClick={() => navigate(c.to!)}
              aria-label={`${c.label}: ${t('alerts.open')}`}
              sx={{ display: 'block', textAlign: 'left', borderRadius: 1, minWidth: 0, '&:focus-visible': { outline: 2, outlineColor: 'primary.main' } }}
            >
              {card}
            </ButtonBase>
          ) : (
            <Box key={c.key} sx={{ minWidth: 0 }}>
              {card}
            </Box>
          );
        })}
      </Box>
      <ContentCard>
        <Tabs data-tour="alerts-tabs" value={filter} onChange={(_, v) => setFilter(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab value="open" label={`${t('alerts.tabOpen')} (${summary?.alerts ?? 0})`} />
          <Tab value="resolved" label={t('alerts.tabResolved')} />
        </Tabs>
        <FilterRow sx={{ mb: 2 }}>
          <TextField
            id="alerts-kind"
            data-tour="alerts-kind"
            select
            size="small"
            label={t('alerts.filterKind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as AlertKind | '')}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            fullWidth
          >
            <MenuItem value="">{t('alerts.allKinds')}</MenuItem>
            {KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`alerts.kind.${k}` as TextKey)}
              </MenuItem>
            ))}
          </TextField>
        </FilterRow>
        <DataTable<AlertItem>
          rows={rows}
          loading={loading && !loaded}
          getRowId={(a) => a.id}
          ariaLabel={t('alerts.title')}
          tourId="alerts"
          searchable
          searchPlaceholder={t('alerts.searchPlaceholder')}
          exportFileName={t('alerts.exportFileName')}
          columns={[
            {
              id: 'subject',
              header: t('alerts.colSubject'),
              value: (a) => subjectName(a),
              render: (a) => (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {subjectName(a)}
                  </Typography>
                  {a.salesRep || a.user ? (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {a.user ? t('alerts.user') : t('alerts.salesRep')}
                      {(a.salesRep ?? a.user)!.username ? ` · ${(a.salesRep ?? a.user)!.username}` : ''}
                    </Typography>
                  ) : (
                    a.client?.idNumber && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {a.client.idNumber}
                      </Typography>
                    )
                  )}
                </Box>
              ),
              sortable: true,
            },
            {
              id: 'severity',
              header: t('alerts.colSeverity'),
              value: (a) => t(`alerts.severity.${a.severity}` as TextKey),
              render: (a) => <StatusChip label={t(`alerts.severity.${a.severity}` as TextKey)} tone={SEVERITY_TONE[a.severity]} />,
              sortable: true,
            },
            { id: 'what', header: t('alerts.colWhat'), value: (a) => describeAlert(a, t), wrap: true, width: 240 },
            {
              id: 'who',
              header: t('alerts.colWho'),
              value: (a) => `${actors(a)}${a.data?.from ? ` · ${t(`alerts.from.${a.data.from}` as TextKey)}` : ''}`,
              hideBelow: 'md',
            },
            { id: 'route', header: t('alerts.colRoute'), value: (a) => [a.route?.name, a.route?.office].filter(Boolean).join(' · ') || '—', hideBelow: 'sm' },
            { id: 'events', header: t('alerts.colEvents'), value: (a) => a.events, align: 'right', sortable: true, hideBelow: 'md' },
            { id: 'when', header: t('alerts.colWhen'), value: (a) => a.lastEventAt, render: (a) => formatDateTime(a.lastEventAt), sortable: true },
            ...(filter === 'resolved' ? [{ id: 'review', header: t('alerts.colReview'), value: (a: AlertItem) => review(a), wrap: true, width: 240 }] : []),
          ]}
          rowActions={(a) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {a.kind !== 'admin_change' && (
                <span data-tour="alert-history">
                  <RowAction
                    label={`${t('alerts.viewChanges')}: ${a.client?.fullName ?? a.entity.id}`}
                    icon={<HistoryOutlinedIcon fontSize="small" />}
                    onClick={() => setHistoryFor(a)}
                  />
                </span>
              )}
              {a.status === 'open' && (
                <PermissionButton
                  allowed={canUpdate}
                  deniedTooltip={t('alerts.noPermission')}
                  tooltip={t('alerts.resolve')}
                  data-tour="alert-resolve"
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ minWidth: 34, px: 0.75 }}
                  onClick={() => {
                    setNote('');
                    setDialog(a);
                  }}
                  aria-label={`${t('alerts.resolve')} ${a.client?.fullName ?? a.entity.id}`}
                >
                  <CheckIcon fontSize="small" />
                </PermissionButton>
              )}
            </Stack>
          )}
          emptyState={
            <EmptyState
              icon={<NotificationsActiveOutlinedIcon />}
              title={filter === 'open' ? t('alerts.emptyOpen') : t('alerts.empty')}
              dense
            />
          }
        />
      </ContentCard>

      {dialog && (
        <StandardModal
          open
          onClose={() => !saving && setDialog(null)}
          title={t('alerts.resolveTitle')}
          subtitle={`${dialog.salesRep?.fullName || dialog.salesRep?.username || dialog.client?.fullName || `#${dialog.entity.id}`} · ${describeAlert(dialog, t)}`}
          maxWidth={520}
          disableClose={saving}
          actions={
            <>
              <Button variant="outlined" color="inherit" onClick={() => setDialog(null)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <LoadingButton variant="contained" color="success" onClick={() => void resolve()} loading={saving}>
                {t('alerts.resolve')}
              </LoadingButton>
            </>
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2">{t('alerts.resolveBody')}</Typography>
            <TextField
              label={t('alerts.note')}
              placeholder={t('alerts.notePlaceholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 300 }}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </StandardModal>
      )}

      <AlertSignalsModal alert={historyFor && historyFor.kind !== 'client_changed' ? historyFor : null} onClose={() => setHistoryFor(null)} />
      <ClientChangeHistoryModal
        open={Boolean(historyFor && historyFor.kind === 'client_changed')}
        onClose={() => setHistoryFor(null)}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        clientData={historyFor ? { id: historyFor.entity.id, fullName: historyFor.client?.fullName ?? null, backupAlertActive: historyFor.status === 'open' } : null}
      />
    </AdminPage>
  );
};

export default AlertCenter;
